/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Phone credential exchange, decoupled from React.
//
// MyAmbits is phone-only: a verified phone credential IS the identity. There
// is no OAuth/email provider and no link-on-collision machinery — Firebase
// Auth enforces one account per phone number, so signInWithCredential lands
// deterministically on that account (creating it on first verification).
//
// The credential itself is minted in PhoneVerificationSheet (native: the
// plugin emits a verificationId via `phoneCodeSent`; web:
// PhoneAuthProvider.verifyPhoneNumber). This module only exchanges it for a
// Firebase `User`. The caller (AuthContext's state machine) drives UI state
// from the returned user — it does NOT depend on onAuthStateChanged, which is
// unreliable on iOS WKWebView + inMemoryPersistence.

import { Capacitor } from "@capacitor/core";
import {
  signInWithCredential,
  type AuthCredential,
  type User,
} from "firebase/auth";
import * as Sentry from "@sentry/react";
import { auth } from "@/config/firebase";
import { logUserAction } from "@/utils/errorLogger";

export type Platform = "ios" | "android" | "web";

export function platform(): Platform {
  const p = Capacitor.getPlatform();
  if (p === "ios" || p === "android") return p;
  return "web";
}

// Defensive timeout wrapper — Build 89 saw cases where signInWithCredential
// would never resolve. Without this, the verification busy state would hang
// indefinitely.
async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label}-timeout`)), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// One automatic retry for transient network/timeout errors. A freshly-cold
// network stack occasionally rejects the first credential exchange with
// auth/network-request-failed; a single 500ms-delayed retry recovers without
// any user-visible glitch.
function isRetryable(err: unknown): boolean {
  const msg = (err as { message?: string })?.message?.toLowerCase() ?? "";
  const code = (err as { code?: string })?.code ?? "";
  return (
    msg.endsWith("-timeout") ||
    msg.includes("network") ||
    code === "auth/network-request-failed"
  );
}

async function withRetry<T>(fn: () => Promise<T>, label: string): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (!isRetryable(err)) throw err;
    Sentry.addBreadcrumb({
      category: "auth",
      message: `${label}.retry`,
      level: "info",
      data: { error: (err as Error)?.message },
    });
    await new Promise((r) => setTimeout(r, 500));
    return fn();
  }
}

// Sign in with a verified phone credential. This is the sole sign-in path:
// Firebase Auth creates the account on first verification and returns the
// existing account on every subsequent one (one account per phone number).
export async function signInWithPhoneCredential(
  phoneCred: AuthCredential,
): Promise<User> {
  Sentry.addBreadcrumb({
    category: "auth",
    message: "phone.signInWithCredential.start",
    level: "info",
  });
  const userCredential = await withRetry(
    () =>
      withTimeout(
        signInWithCredential(auth, phoneCred),
        15_000,
        "phone-credential",
      ),
    "phone.signInWithCredential",
  );
  logUserAction("sign-in", { method: "phone" });
  return userCredential.user;
}
